import { Link, useParams } from 'react-router-dom';
import { usePlayers, useResults } from '@/api/hooks';
import { useAgent } from '@/auth/use-agent';
import { formatDateTime } from '@/lib/format';
import { Card } from '@/ui/Card';
import { ErrorNotice } from '@/ui/ErrorNotice';
import { Loading } from '@/ui/Loading';
import { Notice } from '@/ui/Notice';
import { PlayersTable } from './PlayersTable';
import { ResultsTable } from './ResultsTable';

export function TournamentPage() {
  const { tournamentId = '' } = useParams();
  const { agent } = useAgent();
  const results = useResults(tournamentId);
  const players = usePlayers(tournamentId);

  if (!agent.trackingEnabled) {
    return <Notice>Tracking is not enabled on this environment, so there are no results here.</Notice>;
  }

  const tournament = results.data?.tournament ?? players.data?.tournament;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/" className="text-sm text-blue-700 hover:underline">
          ← Dashboard
        </Link>
        <h1 className="mt-2 text-xl font-semibold">Tournament {tournamentId}</h1>
        {tournament && (
          <p className="mt-1 text-sm text-slate-500">
            {formatDateTime(tournament.startAt)} – {formatDateTime(tournament.endAt)}
          </p>
        )}
      </div>

      <Card
        title="Results"
        description={
          agent.admin
            ? 'Links issued, players who joined, and what they did before, during and after the tournament. One row per agent, in EUR.'
            : 'Your links, the players who joined through them, and what they did before, during and after the tournament. In EUR.'
        }
      >
        {results.isPending && <Loading what="results" />}
        <ErrorNotice error={results.error} />
        {results.data && (
          <>
            <ResultsTable results={results.data} />
            <p className="mt-3 text-xs text-slate-500">
              Active means at least one bet or deposit in the window; rates are against players who joined.{' '}
              {results.data.meta.eventsCount} recorded events.
            </p>
          </>
        )}
      </Card>

      <Card
        title="Players"
        description="Every player who received a link. Windows are the same as above; a player who never joined has no window figures."
      >
        {players.isPending && <Loading what="players" />}
        <ErrorNotice error={players.error} />
        {players.data && <PlayersTable data={players.data} />}
      </Card>
    </div>
  );
}
